import { supabase } from "src/lib/supabaseClient";

// Types for education data
export interface Degree {
  id: number;
  code: string;
  name: string;
  display_name: string;
  level: string;
  description?: string;
  country: string;
}

export interface FieldOfStudy {
  id: number;
  code?: string;
  name: string;
  display_name: string;
  discipline?: string;
  country: string;
}

export interface Country {
  name: string;
  code: string;
}

export interface Institution {
  id: number;
  name: string;
  region: string;
  country: string;
  is_verified: boolean;
  type?: string;
}

export interface EducationLevel {
  id: string;
  name: string;
  order: number;
}


class EducationDataService {
  // Helper method to convert display level names back to database values
  private mapDisplayLevelToDbValue(displayLevel: string): string {
    const levelMapping: { [key: string]: string } = {
      'Undergraduate': 'undergraduate',
      'Graduate': 'graduate', 
      'Doctoral': 'doctoral',
      'Diploma': 'diploma',
      'Post diploma': 'post_diploma',
      'Postgraduate diploma': 'postgraduate_diploma',
      'Other': 'other'
    };
    
    return levelMapping[displayLevel] || displayLevel.toLowerCase();
  }

  // Fetch degrees from database
  async getDegrees(filters?: { country?: string; level?: string }): Promise<Degree[]> {
    try {
      console.log('getDegrees called with filters:', filters);
      let query = supabase.from('degrees').select('*');
      
      if (filters?.country) {
        query = query.eq('country', filters.country);
      }
      
      if (filters?.level) {
        // Convert display level name to database value
        const dbLevel = this.mapDisplayLevelToDbValue(filters.level);
        console.log('Mapping level:', filters.level, '→', dbLevel);
        query = query.eq('level', dbLevel);
      }
      
      const { data, error } = await query.order('display_name').range(0, 1999);
      
      if (error) {
        console.error('Error fetching degrees:', error);
        return [];
      }
      
      // If we got exactly 1000 records, try to get more
      if (data && data.length === 1000) {
        let moreQuery = supabase.from('degrees').select('*');
        
        if (filters?.country) {
          moreQuery = moreQuery.eq('country', filters.country);
        }
        
        if (filters?.level) {
          const dbLevel = this.mapDisplayLevelToDbValue(filters.level);
          moreQuery = moreQuery.eq('level', dbLevel);
        }
        
        const { data: moreData, error: moreError } = await moreQuery
          .order('display_name')
          .range(1000, 1999);
        
        if (!moreError && moreData) {
          const existingIds = new Set(data.map(item => item.id));
          const uniqueMoreData = moreData.filter(item => !existingIds.has(item.id));
          return [...data, ...uniqueMoreData];
        }
      }
      
      console.log('getDegrees returning', data?.length || 0, 'degrees');
      return data || [];
    } catch (error) {
      console.error('Error in getDegrees:', error);
      return [];
    }
  }

  // Fetch fields of study from database
  async getFieldsOfStudy(filters?: { country?: string; discipline?: string }): Promise<FieldOfStudy[]> {
    try {
      console.log('getFieldsOfStudy called with filters:', filters);
      let query = supabase.from('fields_of_study').select('*');
      
      if (filters?.country) {
        query = query.eq('country', filters.country);
      }
      
      if (filters?.discipline) {
        query = query.eq('discipline', filters.discipline);
      }
      
      const { data, error } = await query.order('display_name').range(0, 1999);
      
      if (error) {
        console.error('Error fetching fields of study:', error);
        return [];
      }
      
      // If we got exactly 1000 records, try to get more
      if (data && data.length === 1000) {
        let moreQuery = supabase.from('fields_of_study').select('*');
        
        if (filters?.country) {
          moreQuery = moreQuery.eq('country', filters.country);
        }
        
        if (filters?.discipline) {
          moreQuery = moreQuery.eq('discipline', filters.discipline);
        }
        
        const { data: moreData, error: moreError } = await moreQuery
          .order('display_name')
          .range(1000, 1999);
        
        if (!moreError && moreData) {
          const existingIds = new Set(data.map(item => item.id));
          const uniqueMoreData = moreData.filter(item => !existingIds.has(item.id));
          return [...data, ...uniqueMoreData];
        }
      }
      
      console.log('getFieldsOfStudy returning', data?.length || 0, 'fields');
      return data || [];
    } catch (error) {
      console.error('Error in getFieldsOfStudy:', error);
      return [];
    }
  }

  // Get countries from degrees table
  async getCountries(): Promise<Country[]> {
    try {
      console.log('getCountries called');
      const { data, error } = await supabase
        .from('degrees')
        .select('country')
        .order('country')
        .range(0, 4999);
      
      if (error) {
        console.error('Error fetching countries:', error);
        throw error;
      }
      
      // Extract unique countries and create Country objects
      const uniqueCountries = [...new Set(data?.map(item => item.country) || [])];
      
      const countries: Country[] = uniqueCountries.map(country => ({
        name: country,
        code: country // Using country name as code since we don't have separate codes
      }));
      
      console.log('getCountries returning', countries.length, 'countries');
      return countries;
    } catch (error) {
      console.error('Error in getCountries:', error);
      throw error;
    }
  }

  // Get education levels from degrees table based on country
  async getEducationLevels(country?: string): Promise<EducationLevel[]> {
    try {
      console.log('getEducationLevels called with country:', country);
      
      if (!country) {
        console.log('No country provided, returning empty array');
        return [];
      }

      let query = supabase
        .from('degrees')
        .select('level')
        .eq('country', country);
      
      const { data, error } = await query.range(0, 4999);
      
      if (error) {
        console.error('Error fetching education levels:', error);
        throw error;
      }
      
      // Extract unique levels and create EducationLevel objects
      const uniqueLevels = [...new Set(data?.map(item => item.level) || [])];
      
      // Define level order for sorting
      const levelOrder: { [key: string]: number } = {
        'undergraduate': 1,
        'diploma': 2,
        'post_diploma': 3,
        'graduate': 4,
        'postgraduate_diploma': 5,
        'doctoral': 6,
        'other': 7
      };
      
      const educationLevels: EducationLevel[] = uniqueLevels
        .map(level => ({
          id: level,
          name: level.charAt(0).toUpperCase() + level.slice(1).replace(/_/g, ' '),
          order: levelOrder[level] || 999
        }))
        .sort((a, b) => a.order - b.order);
      
      console.log('getEducationLevels returning', educationLevels.length, 'levels for country:', country);
      return educationLevels;
    } catch (error) {
      console.error('Error in getEducationLevels:', error);
      throw error;
    }
  }

  // Get institutions from database
  async getInstitutions(country?: string): Promise<Institution[]> {
    try {
      console.log('getInstitutions called with country:', country);
      let query = supabase.from('institutions').select('*', { count: 'exact' });
      
      if (country) {
        query = query.eq('country', country);
      }
      
      // Try with explicit range to get all records
      const { data, error, count } = await query.order('name').range(0, 1999);
      
      console.log('getInstitutions query result - count:', count, 'data length:', data?.length);
      
      // If we got exactly 1000 records, there might be more - try to get the rest
      if (data && data.length === 1000) {
        console.log('Got exactly 1000 records, trying to fetch more...');
        let moreQuery = supabase.from('institutions').select('*');
        
        if (country) {
          moreQuery = moreQuery.eq('country', country);
        }
        
        const { data: moreData, error: moreError } = await moreQuery
          .order('name')
          .range(1000, 1999);
        
        if (!moreError && moreData) {
          console.log('Fetched additional', moreData.length, 'records');
          
          // Remove duplicates by ID to avoid React key warnings
          const existingIds = new Set(data.map(item => item.id));
          const uniqueMoreData = moreData.filter(item => !existingIds.has(item.id));
          
          console.log('After removing duplicates:', uniqueMoreData.length, 'unique additional records');
          return [...data, ...uniqueMoreData];
        }
      }
      
      if (error) {
        console.error('Error fetching institutions:', error);
        throw error;
      }
      
      console.log('getInstitutions returning', data?.length || 0, 'institutions');
      return data || [];
    } catch (error) {
      console.error('Error in getInstitutions:', error);
      throw error;
    }
  }

  // Search function for autocomplete
  async searchDegrees(query: string, filters?: { country?: string; level?: string }): Promise<Degree[]> {
    // The getDegrees method already handles level mapping, so we can pass filters directly
    const degrees = await this.getDegrees(filters);
    const lowerQuery = query.toLowerCase();
    
    return degrees.filter(degree => 
      degree.name.toLowerCase().includes(lowerQuery) ||
      degree.display_name.toLowerCase().includes(lowerQuery) ||
      degree.code.toLowerCase().includes(lowerQuery)
    );
  }

  async searchFieldsOfStudy(query: string, filters?: { country?: string }): Promise<FieldOfStudy[]> {
    const fields = await this.getFieldsOfStudy(filters);
    const lowerQuery = query.toLowerCase();
    
    return fields.filter(field => 
      field.name.toLowerCase().includes(lowerQuery) ||
      field.display_name.toLowerCase().includes(lowerQuery) ||
      (field.code && field.code.toLowerCase().includes(lowerQuery))
    );
  }

  async searchCountries(query: string): Promise<Country[]> {
    const countries = await this.getCountries();
    const lowerQuery = query.toLowerCase();
    
    return countries.filter(country => 
      country.name.toLowerCase().includes(lowerQuery) ||
      country.code.toLowerCase().includes(lowerQuery)
    );
  }

  async searchInstitutions(query: string, country?: string): Promise<Institution[]> {
    const institutions = await this.getInstitutions(country);
    const lowerQuery = query.toLowerCase();
    
    return institutions.filter(institution => 
      institution.name.toLowerCase().includes(lowerQuery)
    );
  }

  async searchEducationLevels(query: string, country?: string): Promise<EducationLevel[]> {
    if (!country) {
      return [];
    }
    
    const levels = await this.getEducationLevels(country);
    const lowerQuery = query.toLowerCase();
    
    return levels.filter(level => 
      level.name.toLowerCase().includes(lowerQuery)
    );
  }
}

export const educationDataService = new EducationDataService();